import { useState, useEffect, useCallback, useRef } from 'react';
import gpsWebSocketService from '../services/gpsWebSocketService';
import unifiedApi from '../services/unifiedApi';
import logger from '@/utils/logger';
import { POLLING } from '../config/constants';
import { getSchoolId, setSchoolId } from '@/utils/storage';

const OFFLINE_THRESHOLD_MS = 15 * 60 * 1000;

/**
 * tr_fleet_monitor — logique métier de la page Flotte temps réel.
 * Chargement initial HTTP + mises à jour WebSocket + dérivation statut offline.
 *
 * Retourne : { buses, school, summary, isLoading, error, isConnected,
 *              lastUpdate, isInitialSyncing, refresh }
 */
const useFleetMonitor = () => {
  const [buses, setBuses] = useState(new Map());
  const [school, setSchool] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isInitialSyncing, setIsInitialSyncing] = useState(true);

  const nowRef = useRef(Date.now());
  const offlineTimerRef = useRef(null);
  const syncTimerRef = useRef(null);

  // Toutes les 30s : marquer offline les bus silencieux depuis > 15 min
  useEffect(() => {
    offlineTimerRef.current = setInterval(() => {
      nowRef.current = Date.now();
      setBuses((prev) => {
        const updated = new Map(prev);
        for (const [id, bus] of updated) {
          if (!bus.lastSeen) continue;
          const age = nowRef.current - new Date(bus.lastSeen).getTime();
          if (age > OFFLINE_THRESHOLD_MS && bus.derivedStatus !== 'offline') {
            updated.set(id, { ...bus, derivedStatus: 'offline' });
          }
        }
        return updated;
      });
    }, 30_000);
    return () => clearInterval(offlineTimerRef.current);
  }, []);

  const loadFleet = useCallback(async () => {
    let schoolIdForWs = getSchoolId();
    try {
      const [fleetRes, schoolRes] = await Promise.allSettled([
        unifiedApi.tracking.getFleet(),
        unifiedApi.schools.getCurrent(),
      ]);

      if (fleetRes.status === 'rejected') {
        logger.error('❌ [tr_fleet_monitor] Erreur chargement flotte:', fleetRes.reason);
        setError('Impossible de charger la flotte');
      } else if (fleetRes.value.success) {
        const now = Date.now();
        const map = new Map();
        for (const bus of fleetRes.value.data) {
          const age = bus.lastSeen ? now - new Date(bus.lastSeen).getTime() : Infinity;
          const derivedStatus = age > OFFLINE_THRESHOLD_MS ? 'offline' : bus.derivedStatus;
          map.set(bus.busId, { ...bus, derivedStatus });
        }
        setBuses(map);
        setLastUpdate(new Date());
        logger.debug(`✅ [tr_fleet_monitor] ${map.size} bus chargés`);
      }

      if (schoolRes.status === 'fulfilled') {
        const schoolData = schoolRes.value?.data || schoolRes.value;
        if (schoolData?.id) {
          schoolIdForWs = String(schoolData.id);
          setSchoolId(schoolIdForWs);
        }
        if (schoolData?.latitude && schoolData?.longitude) {
          setSchool(schoolData);
        }
      }
    } catch (err) {
      logger.error('❌ [tr_fleet_monitor] Erreur chargement flotte:', err);
      setError('Impossible de charger la flotte');
    } finally {
      setIsLoading(false);
    }
    return schoolIdForWs || null;
  }, []);

  const applyGPSUpdate = useCallback((data, isActiveTrip) => {
    setIsInitialSyncing(false);
    const busId = data.busId;
    if (!busId) return;

    setBuses((prev) => {
      if (!prev.has(busId)) return prev;
      const existing = prev.get(busId) || {};
      const updated = {
        ...existing,
        busId,
        busNumber: data.busNumber || existing.busNumber,
        schoolId: data.schoolId || existing.schoolId,
        routeId: data.routeId || existing.routeId,
        position: {
          latitude: data.latitude,
          longitude: data.longitude,
          speed: data.speed,
          heading: data.heading,
          altitude: data.altitude ?? null,
          timestamp: data.timestamp,
          ignition: data.ignition ?? null,
          buffered: data.buffered ?? false,
        },
        activeTrip: isActiveTrip
          ? data.tripId
            ? { id: data.tripId, type: data.tripType ?? null }
            : existing.activeTrip
          : null,
        derivedStatus: data.derivedStatus || 'offline',
        lastSeen: data.timestamp || new Date().toISOString(),
      };
      const next = new Map(prev);
      next.set(busId, updated);
      return next;
    });
    setLastUpdate(new Date());
  }, []);

  // Fallback : lève isInitialSyncing après 6s si aucun événement WS
  useEffect(() => {
    syncTimerRef.current = setTimeout(() => setIsInitialSyncing(false), 6000);
    return () => clearTimeout(syncTimerRef.current);
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const schoolId = await loadFleet();
      if (cancelled) return;
      gpsWebSocketService.connect(schoolId || undefined);
    })();

    const onGpsUpdate = (data) => applyGPSUpdate(data, true);
    const onIdleUpdate = (data) => applyGPSUpdate(data, false);

    const onBusOffline = (data) => {
      const busId = data.busId;
      if (!busId) return;
      setIsInitialSyncing(false);
      setBuses((prev) => {
        const existing = prev.get(busId);
        if (!existing) return prev;
        const next = new Map(prev);
        next.set(busId, { ...existing, derivedStatus: 'offline' });
        return next;
      });
      setLastUpdate(new Date());
    };

    gpsWebSocketService.on('gps_update', onGpsUpdate);
    gpsWebSocketService.on('bus_idle_update', onIdleUpdate);
    gpsWebSocketService.on('bus_offline', onBusOffline);

    const statusInterval = setInterval(() => {
      setIsConnected(gpsWebSocketService.getConnectionStatus() === 'connected');
    }, POLLING.FLEET_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(statusInterval);
      gpsWebSocketService.off('gps_update', onGpsUpdate);
      gpsWebSocketService.off('bus_idle_update', onIdleUpdate);
      gpsWebSocketService.off('bus_offline', onBusOffline);
    };
  }, [loadFleet, applyGPSUpdate]);

  const summary = (() => {
    let active = 0,
      service = 0,
      idle = 0,
      parked = 0,
      offline = 0;
    for (const bus of buses.values()) {
      const s = bus.derivedStatus;
      if (s === 'active_moving' || s === 'active_stopped') active++;
      else if (s === 'service_moving' || s === 'service_stopped' || s === 'free_movement' || s === 'idle') service++;
      else if (s === 'parked' || s === 'parked_mid_trip') parked++;
      else offline++;
    }
    return { active, service, idle, parked, offline, total: buses.size };
  })();

  return {
    buses: Array.from(buses.values()),
    school,
    summary,
    isLoading,
    error,
    isConnected,
    lastUpdate,
    isInitialSyncing,
    refresh: loadFleet,
  };
};

export default useFleetMonitor;
